import { Injectable, inject } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Router } from "@angular/router";
import { Observable, throwError } from "rxjs";
import { tap } from "rxjs/operators";

import { LOREN_HEADERS } from "../../constants/loren-headers.constants";
import { LOREN_ROUTES } from "../../constants/loren-routes.constants";
import { environment } from "../../../../environments/environment";
import { LorenLocalStorageSession } from "../../interfaces/loren-local-storage-session.interface";
import { LorenAuthenticationService } from "../../services/loren-authentication-service/loren-authentication.service";
import { LorenUsersEndpointsService } from "../../services/loren-users-endpoints-service/loren-users-endpoints.service";
import { LorenAssetEndpointsService } from "../../../features/loren-assets/services/loren-asset-endpoints-service/loren-asset-endpoints.service";
import { LorenGroupEndpointsService } from "../../../features/loren-groups/services/loren-group-endpoints-service/loren-group-endpoints.service";
import { LorenDeviceEndpointsService } from "../../../features/loren-devices/services/loren-device-endpoints-service/loren-device-endpoints.service";
import { LorenErrorHandlingService } from "../../services/loren-error-handling-service/loren-error-handling-service.service";

@Injectable()
export class LorenHttpInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private authenticationService = inject(LorenAuthenticationService);
  private lorenAssetEndpointsService = inject(LorenAssetEndpointsService);
  private lorenDeviceEndpointsService = inject(LorenDeviceEndpointsService);
  private lorenGroupEndpointsService = inject(LorenGroupEndpointsService);
  private lorenUsersEndpointsService = inject(LorenUsersEndpointsService);
  private lorenErrorHandlingService = inject(LorenErrorHandlingService);


  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {

    const session: LorenLocalStorageSession =
      this.authenticationService.getSession();
    const isApiRequest = request.url.indexOf(environment.apiServerUrl) !== -1;

    if (isApiRequest && session) {
      const cloned = request.clone({
        headers: request.headers.set(
          LOREN_HEADERS.AUTHORIZATION,
          `${session.tokenType} ${session.token}`
        ),
      });

      return this.handleRequest(next, cloned);
    } else {
      return this.handleRequest(next, request);
    }
  }

  private handleRequest(
    next: HttpHandler,
    request: HttpRequest<unknown>
  ): Observable<HttpEvent<unknown>> {
    const shouldShowErrorPage = [
      this.lorenUsersEndpointsService.currentUserPrivileges(),
      this.lorenUsersEndpointsService.userFeatures(),
      this.lorenAssetEndpointsService.assets(),
      this.lorenDeviceEndpointsService.devices(),
      this.lorenGroupEndpointsService.groups(),
    ];

    return next.handle(request).pipe(
      tap({
        next: (res) => {
          if (res instanceof HttpResponse) {
            this.lorenErrorHandlingService.loading.set(false);
          }
        },
        error: (err) => {
          if (this.shouldLogoutAndRedirectToLogin(request, err)) {
            this.authenticationService.logout();
            this.router.navigate([LOREN_ROUTES.LOGIN], {
              queryParams: { returnUrl: location.pathname },
            });
          } else if (err instanceof HttpErrorResponse) {
            if (shouldShowErrorPage.includes(request.url)) {
              this.lorenErrorHandlingService.setGlobalError(err);
            } else {
              return throwError(() => err);
            }
          }
        },
      })
    );
  }

  private shouldLogoutAndRedirectToLogin(
    request: HttpRequest<unknown>,
    err: unknown
  ): boolean {
    const userInfo = this.authenticationService.getUserInfo();
    if (!userInfo) {
      // No session, e.g. 401 on the login page
      return false;
    } else {
      const isChangePasswordRequest =
        request.url.indexOf(
          this.lorenUsersEndpointsService.password(userInfo.id)
        ) !== -1;
      const isResponseUnauthorized =
        err instanceof HttpErrorResponse && err.status === 401;
      // Unauthorized during existing user session and not while changing password -> true, new session needed
      return isResponseUnauthorized && !isChangePasswordRequest;
    }
  }
}
